import { useMemo } from 'react';
import useSelectedTreeItem, {
  TreeNodeRef,
} from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { useQuery } from '@tanstack/react-query';
import { TEST_MANAGEMENT_TESTS } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as sharedApi from 'shared/services/api';
import { Test } from 'shared/types';
import { normalizeCloudTest } from 'testManagement/utils/utils';
import useTableAssets from '@elementary/common-ui/hooks/useTableAssets';
import useCloudTests from 'shared/hooks/useCloudTests';

type Props = {
  includeAlertDestinations?: boolean;
  ignoreTreeView?: boolean;
};

const useTestManagementTableData = ({
  includeAlertDestinations,
  ignoreTreeView,
}: Props = {}): {
  data: Test[];
  isLoading: boolean;
  filterCounters: Record<string, number>;
} => {
  const selectedEnvId = useSelectedEnv();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const {
    data: testManagementTestsData,
    isLoading: isTestManagementTestsLoading,
  } = useQuery(
    [TEST_MANAGEMENT_TESTS, selectedEnvId],
    () => sharedApi.fetchTestManagementTests(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  const { data: tableAssetsExtraData, isFetching: isTableAssetsFetching } =
    useTableAssets();

  const { data: cloudTestsData = [], isLoading: isCloudTestsLoading } =
    useCloudTests({
      includeAlertDestinations: !!includeAlertDestinations,
    });

  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const testsData = useMemo(
    () => [
      ...cloudTestsData
        .filter((t) => !!t.tableAsset)
        .map((test) => normalizeCloudTest(test)),
      ...(testManagementTestsData?.tests || []).map((t) => ({
        ...t,
        source_type: 'dbt',
        severity: t.severity === 'warn' ? 'warning' : t.severity,
        tags: distinctArray([...(t.tags || []), ...(t?.model_tags || [])]),
        owners: distinctArray([
          ...(t.owners || []),
          ...(t?.model_owners || []),
        ]),
        channels: t.alert_destinations?.map((dest) => dest.channel) || [],
        alert_destinations_type: t.alert_destinations?.[0]?.configuration_type, // should be same type for all destinations
        last_data_modification_time: tableAssetsExtraData.get(t?.asset_id ?? '')
          ?.lastDataModificationTime,
        materialization: tableAssetsExtraData.get(t?.asset_id ?? '')
          ?.materialization,
      })),
    ],
    [cloudTestsData, tableAssetsExtraData, testManagementTestsData?.tests],
  );

  const testsByTreeView = useMemo(() => {
    if (ignoreTreeView || !selectedTreeNodeIds) {
      return testsData.sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeNode?.resourceType === 'test') {
      return testsData
        .filter((test) => test.id === selectedTreeNode?.id)
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeFolderNodes?.length) {
      return testsData
        .filter((test) =>
          selectedTreeFolderNodes?.some((node: TreeNodeRef) =>
            node.resourceType === 'test'
              ? node.id === test.id
              : node.id === test.asset_id,
          ),
        )
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeNode) {
      return testsData
        .filter((test) => test.asset_id === selectedTreeNode.id)
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    return [];
  }, [
    selectedTreeNode,
    testsData,
    selectedTreeFolderNodes,
    selectedTreeNodeIds,
    ignoreTreeView,
  ]);

  const filterCounters = useMemo(() => {
    const counterMap = {
      all: testsByTreeView.length,
      singular: 0,
      column: 0,
      table: 0,
    };

    testsByTreeView.forEach((item) => {
      if (item.is_singular) {
        counterMap.singular += 1;
      } else if (item.column) {
        counterMap.column += 1;
      } else {
        counterMap.table += 1;
      }
    });

    return counterMap;
  }, [testsByTreeView]);

  return {
    data: testsByTreeView,
    isLoading:
      isCloudTestsLoading ||
      isTestManagementTestsLoading ||
      isTableAssetsFetching,
    filterCounters,
  };
};

export default useTestManagementTableData;
